/** @format */

import { BreakPoint, MyAccountErrorLogKeys } from '@shared/data-access/enums';
import { Chain, KillSwitches } from '@shared/data-access/interfaces';
import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
	DeviceService,
	MyAccountAnalyticsService,
	Store,
	UtilityService
} from '@shared/data-access/services';

import { FormControl, FormGroup, NgForm, Validators } from '@angular/forms';

import { Subject, forkJoin, of, takeUntil } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

import { HmfModalComponent } from '@dsg-tech/homefield-angular';
import { HttpErrorResponse } from '@angular/common/http';
import { IOrchestratedLists } from '@shared/favorites/data-access/interfaces';
import { Router } from '@angular/router';
import { WishListService } from '@shared/favorites/data-access/services';

@Component({
	selector: 'my-account-list-templates-lists',
	templateUrl: './lists.component.html',
	styleUrl: './lists.component.scss',
	standalone: false
})
export class ListsComponent implements OnInit, OnDestroy {
	@ViewChild(HmfModalComponent) listWelcomeModal: HmfModalComponent | undefined;
	@ViewChild('listsForm') listFormDirective: NgForm;

	showSidePanel = false;
	listForm: FormGroup;
	listError = 'Please enter a list name';
	listName: string;
	listArr: any = [];
	mode = 'create';
	listCreationError = false;
	selectedId: number;
	enableBtn = false;
	listCountErr = false;
	listVisibility = false;
	toggleVisibilityValue = false;
	isMobile = false;
	lists: IOrchestratedLists = null;
	listViewCount = 1;
	showDeletedBanner = false;
	listViewModifier = 0;
	displayedLists = [];
	hasRecommendedList = false;
	private ngUnsubscribe: Subject<void> = new Subject();
	dataDrivenListEnabled = false;
	showBtsDashUpdates = false;
	totalListCount = 0;
	loadingMoreLists = false;

	constructor(
		private chain: Chain,
		private deviceService: DeviceService,
		private analyticsService: MyAccountAnalyticsService,
		private router: Router,
		private store: Store,
		private listService: WishListService,
		private utilityService: UtilityService
	) {}

	ngOnDestroy(): void {
		this.ngUnsubscribe.next();
		this.ngUnsubscribe.complete();
	}

	@HostListener('window:resize')
	onResize(): void {
		this.checkBreakPoint();
	}

	ngOnInit(): void {
		this.analyticsService.triggerAnalyticsEvent('NewPageView');
		this.getKillswitches();
		this.checkBreakPoint();
		this.listForm = new FormGroup({
			name: new FormControl('', { validators: [Validators.required] }),
			formListVisibility: new FormControl()
		});
		const stateData = window.history.state;
		if (stateData && stateData.data && typeof stateData.data.isDeleted !== 'undefined') {
			this.showDeletedBanner = stateData.data.isDeleted;
		}

		this.getLists();
	}

	openSidePanel() {
		if (this.lists._metaData.totalCount >= 25) {
			this.listCountErr = true;
			return;
		}
		this.showSidePanel = true;
		this.listForm.reset();
		this.listFormDirective.resetForm();
		this.listError = '';
	}

	onPanelEvent(event: boolean) {
		this.showSidePanel = event;
	}

	saveList() {
		if (this.listForm.invalid) {
			return;
		}

		this.listName = this.listForm.value.name;
		this.listVisibility = this.toggleVisibilityValue;

		this.listService
			.createOrUpdateList$(this.listName, this.listVisibility)
			.pipe(takeUntil(this.ngUnsubscribe))
			.subscribe({
				next: (list: any) => {
					this.analyticsService.triggerAnalyticsEvent('multiUseAnalyticsEvent', {
						event: 'CreateList'
					});
					this.showSidePanel = false;

					// get last list in response for both scenarios (create default favorites + new list or just a new list)
					const newlyCreatedList = list[list.length - 1];
					const listDisplayName = newlyCreatedList.name.replace('/', '_');

					this.router.navigate([`/MyAccount/Lists/${listDisplayName}`], {
						queryParams: { listID: newlyCreatedList.id }
					});

					// TODO: check updated API response for /v1/mylocker/lists PUT endpoint metaData field for listCount for 'Max List Limit Reached' event
				},
				error: (error: HttpErrorResponse) => {
					this.listCreationError = true;
					this.listError = error.error.message;
					this.listForm.controls['name'].setErrors({ listError: true });
					if (error.status === 422) {
						if (error.error.errorIdentifier === 'EXPLICIT_NAME_EXCEPTION') {
							this.listError =
								'Uh-oh, looks like you used a prohibited word. Please revise & try again.';
						} else if (
							error.error.errorIdentifier === 'DUPLICATE_LIST_NAME_EXCEPTION'
						) {
							this.listError = 'List name already exists.';
						}
					}
				}
			});
	}

	onInputUpdate(event) {
		if (event.source) {
			this.toggleVisibilityValue = event.source.checked;
		}
		this.listCreationError = false;
		this.listError = '';
	}

	getLists() {
		const orchestratedLists = this.listService.getOrchestratedLists$(this.listViewModifier * 9);
		const recommendedList = this.dataDrivenListEnabled
			? this.listService.getRecommendedList$().pipe(
					take(1),
					catchError((err) => {
						this.analyticsService.generateLogError(
							MyAccountErrorLogKeys.MYACCOUNT_LISTS_DASHBOARD,
							'Failed to get recommended list',
							err
						);
						return of(null);
					})
				)
			: of(null);

		forkJoin([orchestratedLists, recommendedList])
			.pipe(takeUntil(this.ngUnsubscribe))
			.subscribe(([orchestratedListsResponse, recommendedListResponse]) => {
				this.displayedLists = this.displayedLists.concat(orchestratedListsResponse.lists);
				this.totalListCount = orchestratedListsResponse._metaData.totalCount;
				this.lists = orchestratedListsResponse;
				this.loadingMoreLists = false;

				if (recommendedListResponse?.list && !this.hasRecommendedList) {
					this.hasRecommendedList = true;
					this.displayedLists.splice(1, 0, recommendedListResponse.list);
				}

				const modalExists = localStorage.getItem('listWelcomeModal');
				if (!modalExists) {
					this.listWelcomeModal?.openModal();
				}
			});
	}

	listWelcomeModalDidOpen() {
		if (this.isMobile) {
			const el = document.getElementById('list-modal-scroll-anchor');
			this.utilityService.scrollToElement(el, null, window.innerHeight / 5);
		}

		this.analyticsService.triggerAnalyticsEvent('ModalOpen', {
			ModalName: 'List Welcome Modal'
		});
	}

	listWelcomeModalDidClose() {
		localStorage.setItem('listWelcomeModal', '1');
	}

	viewMoreClicked() {
		this.loadingMoreLists = true;
		this.listViewModifier++;
		this.loadingMoreLists = true;
		this.getLists();
	}

	determineViewMoreVisibility() {
		let listCount = 0;

		if (this.hasRecommendedList && this.dataDrivenListEnabled) {
			listCount = this.lists?._metaData.totalCount + 1;
		} else {
			listCount = this.lists?._metaData.totalCount;
		}

		return this.displayedLists.length < listCount && this.lists?._metaData.totalCount > 9;
	}

	private checkBreakPoint(): void {
		const breakpoint = this.deviceService.getDeviceBreakPoint(true);
		this.isMobile = breakpoint === BreakPoint.EXTRASMALL || breakpoint === BreakPoint.SMALL;
	}

	private getKillswitches(): void {
		this.store
			.selectDistinctKey<KillSwitches>('ENVIRONMENT_KILLSWITCH_VALUES')
			.pipe(takeUntil(this.ngUnsubscribe))
			.subscribe((killswitch) => {
				this.dataDrivenListEnabled = this.utilityService.checkKillSwitch(
					'dataDrivenListEnabled',
					killswitch,
					this.chain.chainIdentifierAbbr
				);
				this.showBtsDashUpdates = this.utilityService.checkKillSwitch(
					'btsDashUpdates',
					killswitch,
					this.chain.chainIdentifierAbbr
				);
			});
	}
}
